import { observer } from 'owa-mobx-react';
import { default as SelectAllOn } from 'owa-fluent-icons-svg/lib/icons/SelectAllOnRegular';
import { default as SelectAllOff } from 'owa-fluent-icons-svg/lib/icons/SelectAllOffRegular';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { FluentButton } from 'owa-fluent-v9-shims';
import setSelectionSLV from '../mutators/setSelectionSLV';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import resetSelectAllState from '../mutators/resetSelectAllState';
import loc from 'owa-localize';
import { selectLabel } from 'owa-locstrings/lib/strings/selectlabel.locstring.json';
import React from 'react';
import {
    selectAllIconFull,
    selectAllIconMediumAndCompact,
    selectAllIconFullMCL,
    selectAllIconMediumAndCompactMCL,
    full,
    fullDenseless,
    medium,
    compact,
    singleLineCheckboxButton,
    selectAllCheckboxButtonWithLabel,
    selectAllCheckboxButton,
    selectAllCheckboxButtonMedium,
    selectAllCheckboxButtonCompact,
    selectLabelSingleLine,
    buttonChecked,
} from './MailListHeader.scss';

import classnames from 'owa-classnames';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

const MAIL_LIST_SELECT_ALL_SLV_ID = 'MailListSelectAllSLV';

export interface MailListSelectAllProps {
    tableView: any;
    showButtonLabel: boolean;
}

const defaultExport: React.FunctionComponent<MailListSelectAllProps> = observer(
    function MailListSelectAllSLV(props: MailListSelectAllProps) {
        const { tableView, showButtonLabel } = props;
        const isFullDensity = getDensityModeString() === 'full';
        const isRPDenselessEnabled = isGlobalFeatureEnabled('rp-denseless');
        const densityModeCssClass = isRPDenselessEnabled
            ? fullDenseless
            : getDensityModeCssClass(full, medium, compact);

        const iconButtonProps = useComputedValue(() => {
            return {
                iconName:
                    tableView.isInSelectAllStateSLV || tableView.isInCheckedMode
                        ? SelectAllOn
                        : SelectAllOff,
                className: classnames(
                    showButtonLabel
                        ? isFullDensity || isRPDenselessEnabled
                            ? selectAllIconFullMCL
                            : selectAllIconMediumAndCompactMCL
                        : isFullDensity || isRPDenselessEnabled
                        ? selectAllIconFull
                        : selectAllIconMediumAndCompact
                ),
            };
        }, [
            tableView.isInCheckedMode,
            tableView.isInSelectAllStateSLV,
            isFullDensity,
            showButtonLabel,
            isRPDenselessEnabled,
        ]);

        const onCheckboxClick = React.useCallback(() => {
            setSelectionSLV(tableView, !tableView.isInSelectAllStateSLV);
        }, [tableView, tableView.isInSelectAllStateSLV]);

        React.useEffect(() => {
            setSelectionSLV(tableView, tableView.isInCheckedMode);
            resetSelectAllState(tableView);
        }, [tableView, tableView.isInCheckedMode]);

        const onCheckboxKeyDown = React.useCallback(
            (event: React.KeyboardEvent<unknown>) => {
                if (event.key === 'Escape') {
                    setSelectionSLV(tableView, !tableView.isInSelectAllStateSLV);
                }
            },
            [tableView, tableView.isInSelectAllStateSLV]
        );

        if (showButtonLabel) {
            return (
                <div
                    className={classnames(
                        singleLineCheckboxButton,
                        (tableView.isInSelectAllStateSLV || tableView.isInCheckedMode) &&
                            buttonChecked,
                        densityModeCssClass
                    )}
                >
                    <FluentButton
                        appearance="icon"
                        className={classnames(
                            selectAllCheckboxButtonWithLabel,
                            densityModeCssClass
                        )}
                        iconProps={iconButtonProps}
                        onClick={onCheckboxClick}
                        onKeyDown={onCheckboxKeyDown}
                        aria-labelledby={MAIL_LIST_SELECT_ALL_SLV_ID}
                    >
                        <span
                            id={MAIL_LIST_SELECT_ALL_SLV_ID}
                            className={classnames(selectLabelSingleLine, densityModeCssClass)}
                        >
                            {loc(selectLabel)}
                        </span>
                    </FluentButton>
                </div>
            );
        } else {
            return (
                <div className={densityModeCssClass}>
                    <TooltipHost content={loc(selectLabel)}>
                        <FluentButton
                            appearance="icon"
                            className={classnames(
                                selectAllCheckboxButton,
                                (tableView.isInSelectAllStateSLV || tableView.isInCheckedMode) &&
                                    buttonChecked,
                                isRPDenselessEnabled
                                    ? selectAllCheckboxButtonMedium
                                    : getDensityModeCssClass(
                                          undefined,
                                          selectAllCheckboxButtonMedium,
                                          selectAllCheckboxButtonCompact
                                      )
                            )}
                            iconProps={iconButtonProps}
                            onClick={onCheckboxClick}
                            onKeyDown={onCheckboxKeyDown}
                            aria-label={loc(selectLabel)}
                        ></FluentButton>
                    </TooltipHost>
                </div>
            );
        }
    },
    'MailListSelectAllSLV'
);
export default defaultExport;
